import { Box, Button, Dialog, DialogContent, DialogTitle, Slide, Stack, TextField } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { DatePicker } from "@mui/x-date-pickers";
import { useSnackbar } from "notistack";
import React, { ReactElement } from "react";
import { ApiError } from "../../Api/ApiError";
import { EditStudioData, getStudioData } from "../../Api/Store";
import { StudioData } from "../../Objects/StudioInterface";
import ImagePicker from "../atoms/ImagePicker";
import LabelItem from "../atoms/LabelItem";


const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />
})

export interface StudioDataEditDialogProps {
    studioId?: number
    storeId?: number
    onClose: (isEdit: boolean) => void
}

const StudioDataEditDialog: React.FC<StudioDataEditDialogProps> = ({
    studioId,
    storeId,
    onClose,
}) => {
    let { enqueueSnackbar } = useSnackbar()

    const [open, setOpen] = React.useState<boolean>(false)
    const [studioData, setStudioData] = React.useState<StudioData>({})
    const [imageData, setImageData] = React.useState<string>()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const doEdit = React.useCallback(() => {
        EditStudioData({
            studioData: studioData,
            studioImage: imageData
        }, {signal: abortController.signal}).then(() => {
            enqueueSnackbar("スタジオデータを保存しました", { variant: "success" })
            onClose(true)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, studioData, imageData, onClose, enqueueSnackbar])

    React.useEffect(() => {
        const controller = new AbortController()
        setOpen(false)
        if (studioId===0) {
            let data: StudioData = {
                str_id: storeId
            }
            setStudioData( data );
            setImageData(undefined);
            setOpen(true)
        }else if (studioId) {
            getStudioData(studioId, {signal: controller.signal}).then((value: StudioData) => {
                setStudioData(value)
                setImageData(undefined)
                setOpen(true)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
        return () => {
            controller.abort()
        }
    }, [studioId, storeId, enqueueSnackbar])

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            onClose={() => {
                onClose(false)
            }}
            fullWidth
        >
            <DialogTitle>スタジオデータを編集</DialogTitle>
            <DialogContent>
                {studioData &&
                    <Box component="form" onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                        e.preventDefault()
                        doEdit()
                    }} noValidate>
                        <Stack
                            sx={{
                                mb: 2
                            }}
                            spacing={2}
                        >
                            <LabelItem
                                title="スタジオ名/広さ"
                            >
                                <Stack
                                    direction="row"
                                    spacing={2}
                                >
                                    <TextField
                                        variant="standard"
                                        value={studioData.std_name ? studioData.std_name : ""}
                                        fullWidth
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            let data: StudioData = {
                                                ...studioData,
                                                std_name: e.target.value
                                            }
                                            setStudioData(data)
                                        }}
                                    />
                                    <TextField
                                        variant="standard"
                                        value={studioData.std_area ? studioData.std_area : ""}
                                        fullWidth
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            let data: StudioData = {
                                                ...studioData,
                                                std_area: e.target.value
                                            }
                                            setStudioData(data)
                                        }}
                                    />
                                </Stack>
                            </LabelItem>
                            <LabelItem
                                title="稼働開始/終了日"
                            >
                                <Stack
                                    direction="row"
                                    spacing={2}
                                >
                                    <DatePicker
                                        onChange={(newValue: Date | null) => {
                                            let data: StudioData = {
                                                ...studioData,
                                                operation_start_date: newValue ? newValue : undefined
                                            }
                                            setStudioData(data)
                                        }}
                                        value={studioData?.operation_start_date ? studioData?.operation_start_date : null}
                                        renderInput={params => <TextField {...params} fullWidth variant="standard" />}
                                        mask="____/__/__"
                                    />
                                    <DatePicker
                                        onChange={(newValue: Date | null) => {
                                            let data: StudioData = {
                                                ...studioData,
                                                operation_end_date: newValue ? newValue : undefined
                                            }
                                            setStudioData(data)
                                        }}
                                        value={studioData?.operation_end_date ? studioData?.operation_end_date : null}
                                        renderInput={params => <TextField {...params} fullWidth variant="standard" />}
                                        mask="____/__/__"
                                    />
                                </Stack>
                            </LabelItem>
                            <LabelItem
                                title="写真"
                            >
                                <ImagePicker
                                    imageData={imageData ? imageData : (studioData.stdimg_id ? process.env.REACT_APP_API_ENDPOINT + `img/studio/${studioData.stdimg_id}` : undefined)}
                                    onChange={data => {
                                        setImageData(data)
                                    }}
                                />
                            </LabelItem>
                            <LabelItem
                                title="表示順"
                            >
                                <Stack
                                    direction="row"
                                    spacing={2}
                                >
                                    <TextField
                                        variant="standard"
                                        type="number"
                                        value={studioData.sort ? studioData.sort : ""}
                                        fullWidth
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            let data: StudioData = {
                                                ...studioData,
                                                sort: parseInt(e.target.value)
                                            }
                                            setStudioData(data)
                                        }}
                                    />
                                </Stack>
                            </LabelItem>
                        </Stack>
                        <Stack
                            direction="row"
                            justifyContent="flex-end"
                            spacing={2}
                        >
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    onClose(false)
                                }}
                            >キャンセル</Button>
                            <Button
                                variant="outlined"
                                type="submit"
                            >変更を実行</Button>
                        </Stack>
                    </Box>
                }
            </DialogContent>
        </Dialog>
    );
}

export default StudioDataEditDialog